<template>
  <form
    @submit.prevent="
      submit(
        model.data.id
          ? route(`${routePrefix}.${resourceType}.update`, getModelParams(model))
          : localEnabled
          ? route(`${routePrefix}.${resourceType}.bulk-update`)
          : route(`${routePrefix}.${resourceType}.store`)
      )
    "
  >
    <div class="row g-3 mb-2 mt-0">
      <div v-if="model" class="col-12">
        <div v-if="model.form.sections" class="form-container row">
          <div
            v-for="(formFields, section, index) in model.form.sections"
            :key="section"
            class="form-section-container"
            :class="'col-' + (model.schema.config.form?.split ? model.schema.config.form?.split[index] ?? '6' : '12')"
          >
            <div v-if="Object.keys(formFields).length > 0" class="row">
              <h5 v-if="typeof section === 'string' && model.schema.config.sections?.titles">
                {{ trans(`aspects.${aspect}.forms.field-groups.names.${section}`) }}
              </h5>
              <div
                v-for="(width, attribute) in formFields"
                :key="attribute"
                class="col-12"
                :class="getColClass(width, formFields, meta.elementTypes[attribute])"
              >
                <div class="flex align-items-center form-element-container">
                  <div v-if="localEnabled !== null" class="flex-column flex-grow-0 pe-2">
                    <checkbox
                      :name="'enable-edit-' + attribute"
                      :value="localEnabled[attribute] ?? false"
                      @update:data="(checked) => toggleCheckbox(attribute, checked)"
                    ></checkbox>
                  </div>
                  <div class="flex-column flex-grow-1">
                    <form-field
                      v-if="!model.data.relations[attribute] || (meta.media && meta.media[attribute])"
                      :id="resourceType + '-' + attribute"
                      :disabled="attribute === 'id' || (localEnabled && !localEnabled[attribute])"
                      :error="errors ? errors[attribute] : null"
                      :label="model.meta.labels[attribute] ?? trans(`models.${resourceType}.attributes.${attribute}`)"
                      :name="attribute"
                      :choices="meta.options[attribute] ?? []"
                      :property-elements="meta.propertyElements"
                      :relation-type="model.form.relationTypes[attribute]"
                      :required="
                        (!(localEnabled && !localEnabled[attribute]) && model.form.required[attribute]) ?? false
                      "
                      :resource-type="meta.resourceType"
                      :readonly="!resource.meta.editable.includes(attribute)"
                      :field-type="meta.media && meta.media[attribute] ? 'media' : meta.elementTypes[attribute]"
                      :value="resource.data.attributes[attribute]"
                      :element="
                        resource.meta.elements[attribute] ? resource.meta.elements[attribute]['form'] ?? null : null
                      "
                      @update:data="(value) => updateColumn(attribute, value)"
                      @update:media="(media) => updateMedia(media)"
                      @update:model="(attributes, relations) => updateModel(attributes, relations)"
                      @update:toasts="$emit('update:toasts', $event)"
                      @update:attribute="(value) => $emit('update:attribute', attribute, value)"
                    ></form-field>

                    <relation-field
                      v-if="model.data.relations[attribute]"
                      :id="resourceType + '-' + attribute"
                      :key="renderIterator"
                      :disabled="localEnabled && !localEnabled[attribute]"
                      :error="errors ? errors[`${attribute}_id`] : null"
                      :label="model.meta.labels[attribute] ?? trans(`models.${resourceType}.relations.${attribute}`)"
                      :name="attribute"
                      :options="getOptions(attribute)"
                      :relation-title="model.data.relations[attribute].title"
                      :relation-type="model.form.relationTypes[attribute]"
                      :required="
                        (!(localEnabled && !localEnabled[attribute]) && model.form.required[`${attribute}_id`]) ?? false
                      "
                      :value="model.data.attributes[`${attribute}_id`]"
                      @update:data="updateRelation"
                    ></relation-field>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <button v-if="!hideAction" class="btn btn-primary position-relative" style="padding-left: 2.25rem" type="submit">
      {{ getActionTitle(trans) }}
      <i class="fa-solid fa-floppy-disk position-absolute lh-1 start-0 ms-2"></i>
    </button>
  </form>
</template>

<script>
import Checkbox from '@kanrisha/components/Elements/Form/Checkbox';
import FormField from '@kanrisha/components/Form/FormField.vue';
import RelationField from '@kanrisha/components/Form/RelationField.vue';
import { router } from '@inertiajs/vue3';
import useResourceParams from '@kanrisha/composables/useResourceParams';
import useResource from '@kanrisha/composables/useResource';
import useForm from '@kanrisha/composables/useForm';
import useModels from '@kanrisha/composables/useModels';

import { onMounted, ref, watch } from 'vue';

export default {
  components: {
    Checkbox,
    FormField,
    RelationField,
  },
  props: {
    resource: { type: Object, default: () => {} },
    settings: { type: Object, default: () => {} },
    enabledFields: { type: Object, default: () => null }, // todo: explain what this is => BulkEdit
    errors: { type: Object, default: () => {} },
    parentId: { type: Number, default: null },
    options: { type: Object, default: () => {} },
    overwrite: { type: Object, default: () => {} },
    hideAction: { type: Boolean, default: false },
  },
  emits: ['update:attribute', 'updateData', 'update:toasts', 'updateFromApi'],
  setup(props, { emit }) {
    const model = ref(props.resource);
    const localEnabled = ref(props.enabledFields);
    const { adjustments, meta, resourceType } = useResource(props, props.resource);
    const { getModelParams } = useResourceParams(props);
    const { formLevel } = useForm(props, props.resource);
    const renderIterator = ref(0);

    const getActionTitle = (trans) => {
      if (props.resource.meta.config.text.action_title) {
        return props.resource.meta.config.text.action_title;
      }

      if (!model.value.data.id) {
        return trans(`models.${resourceType}.content.create.action`);
      }

      return trans(`models.${resourceType}.content.edit.action`);
    };

    async function submit(url) {
      if (props.resource.meta.config.component.requestType != 'api') {
        if (model.value.data.id) {
          router.patch(url, getEnabledInput(), {});
        } else {
          router.post(url, getEnabledInput(), {});
        }
      } else {
        let { storeModel } = useModels({ resourceType: props.resource.meta.resourceType });
        let storedModel = await storeModel(
          {
            data: {
              id: props.resource.data.id,
              attributes: getEnabledInput(),
            },
          },
          props.resource.meta.resourceType,
          {
            parent: props.options.relation.parentType,
            parentId: props.options.parentResource?.data.id,
            relation: props.options.relation.name,
          }
        );
        emit('updateFromApi', storedModel);
      }

      // remove flagged entries (remove: true) after save, which are not updated automatically due to technical reasons
      // todo: at least move this to a function on its own - but should that be here?
      for (let attribute in model.value.data.attributes) {
        if (meta.value.media && meta.value.media[attribute]) {
          let oldData = model.value.data.attributes[attribute];
          let newData = [];
          for (let key in oldData) {
            if (!oldData[key].remove) {
              newData[key] = oldData[key];
            }
          }
          model.value.data.attributes[attribute] = newData;
        }
      }
    }

    const updateColumn = (column, value) => {
      model.value.data.attributes[column] = value;

      if (
        model.value.form.sections &&
        model.value.form.sections['select'] &&
        model.value.form.sections['select'][column] === 'boolean'
      ) {
        model.value.data.attributes[column] = value === 'true' || value === true ? true : false;
      }

      // ids of the temporary media upload from MediaUpload.vue are added here to the form requests
      if (meta.value.media[column] !== null && typeof meta.value.media[column] !== 'undefined') {
        model.value.data.attributes[`${column}_ids`] = model.value.data.attributes[column]
          .filter((item) => !item.remove)
          .map((item) => item.id);
      }
      emit('updateData', getEnabledInput());
    };

    const updateModel = (attributes, relations) => {
      for (let attribute in attributes) {
        model.value.data.attributes[attribute] = attributes[attribute];
      }
      for (let attribute in relations) {
        let relation = attribute.substring(0, attribute.length - 3);
        model.value.data.relations[relation].id = relations[attribute].id;
        model.value.data.relations[relation].title = relations[attribute].title;
      }
      renderIterator.value++;
      emit('updateData', getEnabledInput());
    };

    const updateRelation = (id, relation, title) => {
      model.value.data.attributes[`${relation}_id`] = id ?? null;
      model.value.data.relations[relation].id = id ?? null;
      model.value.data.relations[relation].title = title;
      emit('updateData', getEnabledInput());
    };

    const getEnabledInput = () => {
      let changes = {};
      for (let attribute in model.value.data.attributes) {
        if (!localEnabled.value || localEnabled.value[attribute] || localEnabled.value[attribute.slice(0, -3)]) {
          changes[attribute] = model.value.data.attributes[attribute];
          if (model.value.data.attributes[attribute + '_id']) {
            changes[attribute + '_id'] = model.value.data.attributes[attribute + '_id'];
          }
        }
      }
      if (props.options?.relation) {
        changes[props.options.relation.relatedKey] = props.options.parentResource.data.id;
        changes.relation = props.options.relation.name;
        changes.parent = props.options.relation.parentType;
        changes.foreign_name = props.options.relatedKey;
      }

      return changes;
    };

    const toggleCheckbox = (column, checked) => {
      localEnabled.value[column] = checked;
      emit('updateData', getEnabledInput());
    };

    const updateMedia = (mediaItems) => {
      mediaItems = ref(mediaItems); // fixme: this is not needed
      model.value.media = mediaItems.value;
      model.value.data.attributes.media_ids = mediaItems.value.filter((item) => !item.remove).map((item) => item.id);
      model.value.data.attributes.remove_media_ids = mediaItems.value
        .filter((item) => item.remove)
        .map((item) => item.id);
    };

    const getOptions = (attribute) => {
      return {
        relation: attribute,
        parent: props.resource.meta.resourceType,
        readonly: !props.resource.meta.editable.includes(`${attribute}_id`),
      };
    };

    const getColClass = (width, formFields, type) => {
      let colClass = 'col-md';

      if (type === 'translatable') {
        return colClass + '12';
      }

      if (Object.keys(formFields).length > 4 || parseInt(width) > 0) {
        return colClass + '-' + (parseInt(width) > 0 ? width : 4);
      }

      return colClass;
    };

    onMounted(() => {
      watch(
        () => props.resource,
        () => {
          model.value = props.resource;
        }
      );
    });

    return {
      adjustments,
      formLevel,
      getActionTitle,
      getColClass,
      getModelParams,
      getOptions,
      localEnabled,
      model,
      meta,
      renderIterator,
      resourceType,
      submit,
      toggleCheckbox,
      updateColumn,
      updateRelation,
      updateModel,
      updateMedia,
    };
  },
};
</script>
